import type { ReactNode } from 'react';

interface ReaderChromeProps {
  visible: boolean;
  clubTitle: string;
  chapterTitle: string;
  onBack: () => void;
  onOpenToc: () => void;
  previousLabel?: string | null;
  nextLabel?: string | null;
  nextLocked?: boolean;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  headerAside?: ReactNode;
}

export function ReaderChrome({
  visible,
  clubTitle,
  chapterTitle,
  onBack,
  onOpenToc,
  previousLabel,
  nextLabel,
  nextLocked = false,
  canGoPrevious = false,
  canGoNext = false,
  onPrevious,
  onNext,
  headerAside,
}: ReaderChromeProps) {
  return (
    <>
      <div
        aria-hidden="true"
        className={[
          'pointer-events-none absolute inset-0 bg-[rgba(17,12,6,0.08)] transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      />

      <div
        className={`absolute inset-x-0 top-0 z-20 transition-transform duration-200 ${visible ? 'translate-y-0' : '-translate-y-full'}`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div
          data-testid="reader-chrome-top-bar"
          className="mx-3 flex min-h-11 items-center gap-2 rounded-[18px] border border-white/68 bg-[rgba(255,251,244,0.9)] px-2.5 py-1.5 shadow-[0_10px_18px_rgba(41,30,14,0.08)] backdrop-blur-xl min-[430px]:mx-4 min-[768px]:mx-auto min-[768px]:w-[calc(100%-var(--desktop-gutter)*2)] min-[768px]:max-w-[var(--reader-media-max-width)]"
        >
          <button
            onClick={onBack}
            className="flex h-[1.875rem] w-[1.875rem] shrink-0 items-center justify-center rounded-full bg-white/92 text-[rgba(26,26,26,0.68)] shadow-[0_4px_10px_rgba(41,30,14,0.05)]"
            aria-label="Назад"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-tight text-[rgba(26,26,26,0.8)]">
              {clubTitle}
            </p>
            <p className="mt-0.5 truncate text-[10.5px] font-medium leading-tight text-[rgba(26,26,26,0.46)]">
              {chapterTitle}
            </p>
          </div>

          {headerAside}

          <button
            onClick={onOpenToc}
            className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(26,26,26,0.08)] bg-white/88 px-2.25 py-1.25 text-[12.5px] font-semibold text-[rgba(26,26,26,0.62)]"
            aria-label="Главы"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="15" y2="18" />
            </svg>
            <span>Главы</span>
          </button>
        </div>
      </div>

      <div
        className={`absolute inset-x-0 bottom-0 z-20 transition-transform duration-200 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div
          data-testid="reader-chrome-bottom-bar"
          className="mx-3 rounded-[22px] border border-white/70 bg-[rgba(255,251,244,0.95)] px-2.5 py-2.5 shadow-[0_10px_20px_rgba(41,30,14,0.1)] backdrop-blur-xl min-[430px]:mx-4 min-[768px]:mx-auto min-[768px]:w-[calc(100%-var(--desktop-gutter)*2)] min-[768px]:max-w-[var(--reader-media-max-width)]"
        >
          <div className="flex items-center gap-2.5">
            <button
              disabled={!canGoPrevious}
              onClick={onPrevious}
              title={previousLabel ?? 'Пред. глава'}
              aria-label="Пред. глава"
              className="inline-flex min-h-[2.625rem] flex-1 items-center justify-center gap-2 rounded-full border border-[rgba(26,26,26,0.08)] bg-white/88 px-4 text-[12.5px] font-semibold text-[rgba(26,26,26,0.62)] shadow-[0_5px_10px_rgba(41,30,14,0.05)] disabled:opacity-30"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span className="truncate">Пред. глава</span>
            </button>

            <button
              disabled={!canGoNext}
              onClick={onNext}
              title={nextLabel ?? 'След. глава'}
              aria-label="След. глава"
              className="inline-flex min-h-[2.625rem] flex-1 items-center justify-center gap-2 rounded-full bg-[var(--primary)] px-4 text-[12.5px] font-semibold text-[var(--primary-foreground)] shadow-[0_10px_18px_rgba(107,78,230,0.16)] disabled:opacity-30"
            >
              <span className="truncate">След. глава</span>
              {nextLocked ? (
                <span aria-hidden="true">🔒</span>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
